import moment from 'moment';
import _ from 'underscore';
import angular from 'angular';

function MapperService(dateUtilsService, visibilityHelpersService) {
  this.ERRORS = {
    DATE_DATE_OR_MOMENT: 'date must be a Date or Moment object',
    DATE_START_DATE_OR_MOMENT: 'dateStart must be a Date or Moment object',
    DATE_START_INVALID_DATE: 'dateStart is not a valid date',
    DATE_END_DATE_OR_MOMENT: 'dateEnd must be a Date or Moment object',
    DATE_END_INVALID_DATE: 'dateEnd is not a valid date',
    DATE_INVALID_DATE: 'date is not a valid date',
    CREATED_INVALID_DATE: 'created is not a valid date',
    //event mapping errors
    RECURRING_END_DATE_INVALID_DATE: 'recurring end date is not a valid date',
    TAGS_MUST_BE_AN_ARRAY: 'tags must be an array'
  };

  /**
   * @param {Object} organization - organization data to map to API representation
   */
  this.mapOrganizationToApi = function(organization) {
    if (!organization) throw new Error('mapperService - mapOrganizationToUI() - organization is required');

    const data = {};

    if ('name' in organization) data.name = organization.name;
    if ('address' in organization) data.address = organization.address;
    if ('city' in organization) data.city = organization.city;
    if ('state' in organization) data.state = organization.state;
    if ('zip' in organization) data.zip = organization.zip;

    return data;
  };

  /**
   * @param {Object} organization - organization data to map to UI representation
   */
  this.mapOrganizationToUI = function(organization) {
    if (!organization) throw new Error('mapperService - mapOrganizationToUI() - organization is required');

    const data = {};

    data.id = organization.id;
    data.name = organization.name;
    data.address = organization.address;
    data.city = organization.city;
    data.state = organization.state;
    data.zip = organization.zip;
    if (organization.created) {
      data.created = _createUTCMomentFromIsoOrUnix(organization.created);
      if (!data.created.isValid()) throw new Error(this.ERRORS.CREATED_INVALID_DATE);
    }

    return data;
  };

  /**
   * @param {Object} event - event data to map to API representation
   * @param {Object} [options]
   * @param {Boolean} [options.create] - map for event create
   */
  this.mapEventToApi = function(event, options = {}) {
    if (!event) throw new Error('mapperService - mapEventToApi() - event is required');

    const data = {};

    if ('venue' in event) data.venue = event.venue;
    if ('name' in event) data.name = event.name;
    if ('enable' in event) data.enable = event.enable;
    if ('visibility' in event) data.visibility = Number(event.visibility);
    if ('inventoryNote' in event) data.inventoryNote = event.inventoryNote;
    if ('trackingFacebookPixelId' in event) data.trackingFacebookPixelId = event.trackingFacebookPixelId;

    if ('performers' in event) {
      if (event.performers && event.performers.length) {
        data.performers = _.map(event.performers, _.clone);
      } else {
        data.performers = [];
      }
    }

    if ('primaryImage' in event) {
      data.images = [{
        filename: event.primaryImage
      }];
    }
    if ('headerImage' in event) {
      data.headerImage = {
        filename: event.headerImage,
        small: event.headerImage,
        medium: event.headerImage,
        large: event.headerImage,
        xlarge: event.headerImage
      };
    }
    if (event.enableRecurring) {
      data.recurring = {
        enable: event.enableRecurring,
        config: {
          days: [
            moment(event.date).day()
          ],
          type: 'weekly'
        }
      };

      if (moment.isMoment(event.recurringEndDate)) {
        data.recurring.config.endDate = dateUtilsService.toUTCTimestampWithoutTime(event.recurringEndDate);
      } else {
        data.recurring.config.endDate = null;
      }
    } else if (event.recurringEndDate) {
      if (_.isDate(event.recurringEndDate)) {
        data.recurringEndDate = dateUtilsService.toUTCTimestampWithoutTime(event.recurringEndDate);
      } else {
        data.recurringEndDate = null;
      }
    }

    if ('date' in event) {
      if (!_.isDate(event.date) && !moment.isMoment(event.date)) throw new Error(this.ERRORS.DATE_DATE_OR_MOMENT);
      event.date = _.isDate(event.date) ? moment.utc(event.date.toISOString()) : event.date;
      data.date = dateUtilsService.toUTCTimestampWithoutTime(event.date);
    }
    if ('dateStart' in event) {
      if (!_.isDate(event.dateStart) && !moment.isMoment(event.dateStart)) throw new Error(this.ERRORS.DATE_START_DATE_OR_MOMENT);
      event.dateStart = _.isDate(event.dateStart) ? moment.utc(event.dateStart.toISOString()) : event.dateStart;
      data.dateStart = dateUtilsService.toUTCTimestampWithoutTime(event.dateStart);
      if ('dateStartTime' in event) {
        const dateStart = moment.utc(data.dateStart, 'x');
        data.dateStart = moment.utc(`${dateStart.format('MM/DD/YYYY')} ${event.dateStartTime}`);
        data.dateStart = dateUtilsService.toUTCTimestampWithTime(data.dateStart);
      }
    }
    if ('dateEnd' in event) {
      if (!_.isDate(event.dateEnd) && !moment.isMoment(event.dateEnd)) throw new Error(this.ERRORS.DATE_END_DATE_OR_MOMENT);
      event.dateEnd = _.isDate(event.dateEnd) ? moment.utc(event.dateEnd.toISOString()) : event.dateEnd;
      data.dateEnd = dateUtilsService.toUTCTimestampWithoutTime(event.dateEnd);
      if ('dateEndTime' in event) {
        const dateEnd = moment.utc(data.dateEnd, 'x');
        data.dateEnd = moment.utc(`${dateEnd.format('MM/DD/YYYY')} ${event.dateEndTime}`);
        data.dateEnd = dateUtilsService.toUTCTimestampWithTime(data.dateEnd);
      }
    }
    if ('enabledOn' in event) {
      if (!event.enabledOn || !_.isDate(event.enabledOn) && !moment.isMoment(event.enabledOn)) {
        data.enabledOn = null;
      } else {
        event.enabledOn = _.isDate(event.enabledOn) ? moment(event.enabledOn.toISOString()) : event.enabledOn;
        if ('enabledOnTime' in event) {
          data.enabledOn = moment(`${event.enabledOn.format('MM/DD/YYYY')} ${event.enabledOnTime}`);
        }
        data.enabledOn = data.enabledOn.toDate().getTime();
      }
    }

    if ('isMapWidgetEnabled' in event) data.isMapWidgetEnabled = event.isMapWidgetEnabled;
    if ('externalSellingLink' in event) data.externalSellingLink = event.externalSellingLink ? _convertToUrl(event.externalSellingLink) : event.externalSellingLink;

    if (('description' in event) || ('youtube' in event) || ('minAge' in event)) {
      if (options.creating) {
        if ('description' in event) data.description = event.description;
        if ('youtube' in event) data.youtube = event.youtube ? _convertToUrl(event.youtube) : event.youtube;
        if ('minAge' in event) data.minAge = event.minAge;
      } else {
        data.details = {};
        if ('description' in event) data.details.description = event.description;
        if ('youtube' in event) data.details.youtube = event.youtube ? _convertToUrl(event.youtube) : event.youtube;
        if ('minAge' in event) data.details.minAge = event.minAge;
      }
    }
    if (('tags' in event) || ('musicTags' in event) || ('dressCodeOkTags' in event) || ('dressCodeNotOkTags' in event)) {
      data.tags = [];
      if ('tags' in event) {
        if (!_.isArray(event.tags)) throw new Error(this.ERRORS.TAGS_MUST_BE_AN_ARRAY);
        data.tags = data.tags.concat(event.tags);
      }
      if ('musicTags' in event) {
        if (!_.isArray(event.musicTags)) throw new Error(this.ERRORS.TAGS_MUST_BE_AN_ARRAY);
        data.tags = data.tags.concat(event.musicTags);
      }
      if ('dressCodeOkTags' in event) {
        if (!_.isArray(event.dressCodeOkTags)) throw new Error(this.ERRORS.TAGS_MUST_BE_AN_ARRAY);
        data.tags = data.tags.concat(event.dressCodeOkTags);
      }
      if ('dressCodeNotOkTags' in event) {
        if (!_.isArray(event.dressCodeNotOkTags)) throw new Error(this.ERRORS.TAGS_MUST_BE_AN_ARRAY);
        data.tags = data.tags.concat(event.dressCodeNotOkTags);
      }
      data.tags = _.uniq(data.tags, tag => {
        return tag.value;
      });
    }

    return data;
  };

  /**
   * @param {Object} event - event data to map to UI representation
   */
  this.mapEventToUI = function(event) {
    if (!event) throw new Error('mapperService - mapEventToUI() - event is required');

    const data = {};

    data.id = event.id;
    data.name = event.name;
    data.venue = event.venue;
    data.enable = event.enable;
    data.visibility = event.visibility;
    data.seoPathname = event.seoPathname;
    data.inventoryNote = event.inventoryNote;
    data.trackingFacebookPixelId = event.trackingFacebookPixelId;

    if (event.performers) {
      data.performers = _.map(event.performers, performer => {
        return performer;
      });
    }

    data.published = visibilityHelpersService.isPublished(event);

    if (event.date) {
      data.date = _createUTCMomentFromIsoOrUnix(event.date);
      if (!data.date.isValid()) throw new Error(this.ERRORS.DATE_INVALID_DATE);
      data.dateMonth = data.date.format('MMMM');
      data.dateDayOfMonth = data.date.format('D');
      data.dateDayOfWeek = data.date.format('dddd');
    }
    if (event.recurring) {
      data.enableRecurring = event.recurring.enable;

      if (event.recurring.config && event.recurring.config.endDate) {
        data.recurringEndDate = _createUTCMomentFromIsoOrUnix(event.recurring.config.endDate);
      }
    }
    if (event.dateStart) {
      data.dateStart = _createUTCMomentFromIsoOrUnix(event.dateStart);
      data.dateStartTime = data.dateStart.format('h:mm A');
      if (!data.dateStart.isValid()) throw new Error(this.ERRORS.DATE_START_INVALID_DATE);
    }
    if (event.dateEnd) {
      data.dateEnd = _createUTCMomentFromIsoOrUnix(event.dateEnd);
      data.dateEndTime = data.dateEnd.format('h:mm A');
      if (!data.dateEnd.isValid()) throw new Error(this.ERRORS.DATE_END_INVALID_DATE);
    }
    if (event.enabledOn) {
      data.enabledOn = moment(event.enabledOn);
      data.enabledOnTime = data.enabledOn.format('h:mm A');
      if (!data.enabledOn.isValid()) throw new Error(this.ERRORS.DATE_END_INVALID_DATE);
    }

    data.externalSellingLink = event.externalSellingLink;
    data.isMapWidgetEnabled = event.isMapWidgetEnabled;

    if (event.details) {
      data.description = event.details.description;
      data.youtube = event.details.youtube;
      data.minAge = event.details.minAge;
    }
    if (event.primaryImage) {
      data.primaryImage = event.primaryImage.filename;
    }
    if (event.headerImage) {
      data.headerImage = event.headerImage.filename;
    }
    if (event.tags) {
      if (!_.isArray(event.tags)) throw new Error(this.ERRORS.TAGS_MUST_BE_AN_ARRAY);
      data.tags = event.tags;
      data.musicTags = _.filter(data.tags, tag => {
        return tag.key === 'MUSIC';
      });
      data.dressCodeOkTags = _.filter(data.tags, tag => {
        return tag.key === 'DRESS_CODE_OK';
      });
      data.dressCodeNotOkTags = _.filter(data.tags, tag => {
        return tag.key === 'DRESS_CODE_NOT_OK';
      });
    }

    if (data.visibility === 0) {
      data.isPrivate = true;
    } else {
      data.isPrivate = false;
    }

    return data;
  };

  this.mapServerToApi = function(server) {
    if (!server) throw new Error('mapperService - mapServerToApi() - server is required');

    const data = {};

    if ('organizationId' in server) data.organizationId = server.organizationId;
    if ('firstName' in server) data.firstName = server.firstName;
    if ('lastName' in server) data.lastName = server.lastName;

    return data;
  };

  this.mapServerToUI = function(server) {
    if (!server) throw new Error('mapperService - mapServerToUI() - server is required');

    const data = {};

    data.id = server.id;
    data.name = `${server.firstName} ${server.lastName}`;
    data.firstName = server.firstName;
    data.lastName = server.lastName;

    return data;
  };

  this.mapInventoryToUI = function(inventory) {
    const data = angular.copy(inventory);

    data.price = data.price || 0;

    data.date = moment.utc(data.date, 'x');
    data.userPaysProcessing = inventory.userPaysProcessing;
    data.syncQuantityBySection = inventory.syncQuantityBySection;
    data.membershipLevel = inventory.membershipLevel;
    data.membershipUsersOnly = data.membershipLevel >= 50;
    data.disabled = !inventory.enable;

    if (inventory.recurring) {
      data.enableRecurring = inventory.recurring.enable;

      if (inventory.recurring.config && inventory.recurring.config.endDate) {
        data.recurringEndDate = _createUTCMomentFromIsoOrUnix(inventory.recurring.config.endDate);
      }
    }

    if (data.sellingStart) {
      const sellingStart = moment.utc(data.sellingStartDisplay, 'x');
      data.sellingStartDate = sellingStart;
      data.sellingStartTime = sellingStart.format('h:mm A');
    }

    if (data.sellingEnd) {
      const sellingEnd = moment.utc(data.sellingEndDisplay, 'x');
      data.sellingEndDate = sellingEnd;
      data.sellingEndTime = sellingEnd.format('h:mm A');
    }

    if (data.checkInStart) {
      const checkInStart = moment.utc(data.checkInStart, 'x');
      data.checkInStartTime = checkInStart.format('h:mm A');
    }

    if (data.checkInEnd) {
      const checkInEnd = moment.utc(data.checkInEnd, 'x');
      data.checkInEndTime = checkInEnd.format('h:mm A');
    }

    if (data.start) {
      const start = moment.utc(data.start, 'x');
      data.start = start.format('h:mm A');
    }

    if (data.end) {
      const end = moment.utc(data.end, 'x');
      data.end = end.format('h:mm A');
    }

    if (data.fees) {
      data.fees = _.map(data.fees, fee => {
        if (fee.type === 'PERCENT') {
          fee.amount = Number((fee.amount * 100).toFixed(2));
        }
        return fee;
      });
    }

    data.tax = Number(((data.tax || 0) * 100).toFixed(4));
    data.tip = Number(((data.tip || 0) * 100).toFixed(2));

    return data;
  };

  this.mapInventoryToApi = function(inventory) {
    const data = {};

    if ('name' in inventory) data.name = inventory.name;
    if ('type' in inventory) data.type = inventory.type;
    if ('disabled' in inventory) data.enable = !inventory.disabled;
    if ('price' in inventory) data.price = inventory.price;
    if ('quantity' in inventory) data.quantity = inventory.quantity;
    if ('purchaseMin' in inventory) data.purchaseMin = inventory.purchaseMin;
    if ('purchaseMax' in inventory) data.purchaseMax = inventory.purchaseMax;
    if ('guests' in inventory) data.guests = inventory.guests;
    if ('day' in inventory) data.day = inventory.day;
    if ('description' in inventory) data.description = inventory.description;
    // if ('termsAndConditions' in inventory) {
    //   if (inventory.termsAndConditions && inventory.termsAndConditions.length && inventory.termsAndConditions.trim() !== '') {
    //     data.termsAndConditions = inventory.termsAndConditions;
    //   } else {
    //     data.termsAndConditions = null;
    //   }
    // }
    if ('date' in inventory) data.date = inventory.date;
    if ('userPaysProcessing' in inventory) data.userPaysProcessing = inventory.userPaysProcessing;
    if ('selectBottles' in inventory) data.selectBottles = inventory.selectBottles;
    if ('reservationEnabled' in inventory) data.reservationEnabled = inventory.reservationEnabled;
    if ('venueLayoutSection' in inventory) data.venueLayoutSection = inventory.venueLayoutSection;
    if ('syncQuantityBySection' in inventory) data.syncQuantityBySection = inventory.syncQuantityBySection;
    if ('membershipLevel' in inventory) data.membershipLevel = inventory.membershipLevel;
    if ('paymentType' in inventory) data.paymentType = inventory.paymentType;
    if ('depositAmount' in inventory) data.depositAmount = inventory.depositAmount;
    if ('membershipLevel' in inventory) data.membershipLevel = inventory.membershipLevel;
    if ('membershipUsersOnly' in inventory) {
      if (inventory.membershipUsersOnly) {
        data.membershipLevel = 50;
      } else {
        data.membershipLevel = 0;
      }
    }
    if ('membershipPerkType' in inventory) data.membershipPerkType = inventory.membershipPerkType;

    if (inventory.enableRecurring) {
      data.recurring = {
        enable: inventory.enableRecurring,
        config: {
          days: [
            moment(inventory.date).day()
          ],
          type: 'weekly'
        }
      };

      if (moment.isMoment(inventory.recurringEndDate)) {
        data.recurring.config.endDate = dateUtilsService.toUTCTimestampWithoutTime(inventory.recurringEndDate);
      } else {
        data.recurringEndDate = null;
      }
    } else if (inventory.recurringEndDate) {
      if (_.isDate(inventory.recurringEndDate)) {
        data.recurringEndDate = dateUtilsService.toUTCTimestampWithoutTime(inventory.recurringEndDate);
      } else {
        data.recurringEndDate = null;
      }
    }

    if ('tax' in inventory) {
      if (_.isNull(inventory.tax) || _.isUndefined(inventory.tax)) {
        data.tax = null;
      } else {
        data.tax = Number((inventory.tax / 100).toFixed(6));
      }
    }
    if ('tip' in inventory) {
      if (_.isNull(inventory.tip) || _.isUndefined(inventory.tip)) {
        data.tip = null;
      } else {
        data.tip = Number((inventory.tip / 100).toFixed(4));
      }
    }

    if ('fees' in inventory) {
      if (inventory.fees && inventory.fees.length) {
        const formFees = _.map(inventory.fees, _.clone); //Make a clone of the array of fees

        data.fees = _.map(formFees, fee => {
          if (fee.amount) {
            if (fee.type === 'PERCENT') {
              fee.amount = Number((fee.amount / 100).toFixed(4));
            }
          } else {
            fee.amount = 0;
          }
          return fee;
        });
      } else {
        data.fees = [];
      }
    }

    const sellingStartDate = inventory.sellingStartDate;
    const sellingStartTime = inventory.sellingStartTime;
    if (sellingStartDate && sellingStartTime) {
      const sellingStart = moment.utc(`${sellingStartDate.format('MM/DD/YYYY')} ${sellingStartTime}`, 'MM/DD/YYYY h:mm A');

      data.sellingStart = dateUtilsService.toUTCTimestampWithTime(sellingStart);
    }
    delete data.sellingStartDate;
    delete data.sellingStartTime;

    const sellingEndDate = inventory.sellingEndDate;
    const sellingEndTime = inventory.sellingEndTime;
    if (sellingEndDate && sellingEndTime) {
      const sellingEnd = moment.utc(`${sellingEndDate.format('MM/DD/YYYY')} ${sellingEndTime}`, 'MM/DD/YYYY h:mm A');

      data.sellingEnd = dateUtilsService.toUTCTimestampWithTime(sellingEnd);
    }
    delete data.sellingEndDate;
    delete data.sellingEndTime;

    if (inventory.checkInStartTime) {
      const checkInStart = moment.utc(`${moment.utc().format('MM/DD/YYYY')} ${inventory.checkInStartTime}`, 'MM/DD/YYYY h:mm A');
      data.checkInStart = dateUtilsService.toUTCTimestampWithTime(checkInStart);
    }

    if (inventory.checkInEndTime) {
      const checkInEnd = moment.utc(`${moment.utc().format('MM/DD/YYYY')} ${inventory.checkInEndTime}`, 'MM/DD/YYYY h:mm A');
      data.checkInEnd = dateUtilsService.toUTCTimestampWithTime(checkInEnd);
    }

    if (inventory.start) {
      const start = moment.utc(`${moment.utc().format('MM/DD/YYYY')} ${inventory.start}`, 'MM/DD/YYYY h:mm A');

      data.start = dateUtilsService.toUTCTimestampWithTime(start);
      data.end = dateUtilsService.toUTCTimestampWithTime(start);
    }

    if (inventory.end) {
      const end = moment.utc(`${moment.utc().format('MM/DD/YYYY')} ${inventory.end}`, 'MM/DD/YYYY h:mm A');
      data.end = dateUtilsService.toUTCTimestampWithTime(end);
    }

    return data;
  };

  this.mapVenueToApi = function(venue, tags) {
    if (!venue) return null;

    const data = {};

    if ('name' in venue) data.name = venue.name;
    if ('logo' in venue) data.logo = venue.logo;
    if ('phone' in venue) data.phone = venue.phone;
    if ('email' in venue) data.email = venue.email;
    if ('website' in venue) data.website = venue.website;
    if ('shortDescription' in venue) data.shortDescription = venue.shortDescription;
    if ('description' in venue) data.description = venue.description;
    if ('minAge' in venue) data.minAge = venue.minAge;
    if ('facebook' in venue) data.facebook = venue.facebook;
    if ('twitter' in venue) data.twitter = venue.twitter;
    if ('instagram' in venue) data.instagram = venue.instagram;
    if ('schedule' in venue) data.schedule = venue.schedule;
    if ('showMarketingOptIn' in venue) data.showMarketingOptIn = venue.showMarketingOptIn;
    if ('marketingOptInText' in venue) data.marketingOptInText = venue.marketingOptInText;
    if ('inventoryTimeframe' in venue) data.inventoryTimeframe = venue.inventoryTimeframe;
    if ('inventorySortOrder' in venue) data.inventorySortOrder = venue.inventorySortOrder;
    if ('trackingFacebookPixelId' in venue) data.trackingFacebookPixelId = venue.trackingFacebookPixelId;

    if ('tax' in venue) data.tax = Number((venue.tax / 100).toFixed(6));
    if ('ticketTax' in venue) data.ticketTax = Number((venue.ticketTax / 100).toFixed(6));
    if ('tip' in venue) data.tip = Number((venue.tip / 100).toFixed(4));

    if ('tableTerms' in venue) data.tableTerms = venue.tableTerms;
    if ('ticketTerms' in venue) data.ticketTerms = venue.ticketTerms;

    if ('fees' in venue) {
      if (venue.fees && venue.fees.length) {
        const formFees = _.map(venue.fees, _.clone); //Make a clone of the array of fees

        data.fees = _.map(formFees, fee => {
          if (fee.amount) {
            if (fee.type === 'PERCENT') {
              fee.amount = Number((fee.amount / 100).toFixed(4));
            }
          } else {
            fee.amount = 0;
          }
          return fee;
        });
      } else {
        data.fees = [];
      }
    }
    if ('ticketFees' in venue) {
      if (venue.ticketFees && venue.ticketFees.length) {
        const formFees = _.map(venue.ticketFees, _.clone); //Make a clone of the array of ticketFees

        data.ticketFees = _.map(formFees, fee => {
          if (fee.amount) {
            if (fee.type === 'PERCENT') {
              fee.amount = Number((fee.amount / 100).toFixed(4));
            }
          } else {
            fee.amount = 0;
          }
          return fee;
        });
      } else {
        data.ticketFees = [];
      }
    }

    if (('primaryColor' in venue) || ('secondaryColor' in venue)) {
      data.branding = {};
      if (('primaryColor' in venue)) data.branding.primaryColor = _convertRgbToApiObj(venue.primaryColor);
      if (('secondaryColor' in venue)) data.branding.secondaryColor = _convertRgbToApiObj(venue.secondaryColor);
    }

    if ('venueEmail' in venue) data.email = venue.venueEmail;
    if ('notificationEmails' in venue) data.notificationEmails = _getNotificationEmails(venue.notificationEmails);
    if (venue.defaultPoster) {
      if (!data.config) data.config = {};
      data.config.defaultPoster = {};
      data.config.defaultPoster.small = venue.defaultPoster;
      data.config.defaultPoster.medium = venue.defaultPoster;
      data.config.defaultPoster.large = venue.defaultPoster;
      data.config.defaultPoster.xlarge = venue.defaultPoster;
      data.config.defaultPoster.filename = venue.defaultPoster;
    }

    if ('reservationNote' in venue) {
      if (!data.config) data.config = {};
      data.config.reservationNote = venue.reservationNote;
    }

    if ('guestlistNote' in venue) {
      if (!data.config) data.config = {};
      data.config.guestlistNote = venue.guestlistNote;
    }

    if ('reservationChitText' in venue) {
      if (!data.config) data.config = {};
      data.config.reservationChitText = venue.reservationChitText;
    }

    if ('reservationChitTip' in venue) {
      if (!data.config) data.config = {};
      data.config.reservationChitTip = venue.reservationChitTip;
    }

    if ('posAutoClose' in venue) {
      if (!data.config) data.config = {};
      data.config.posAutoClose = venue.posAutoClose;
    }

    if ('defaultReservationRequestEnabled' in venue) {
      if (!data.config) data.config = {};
      data.config.defaultReservationRequestEnabled = venue.defaultReservationRequestEnabled;
    }

    if ('defaultGuestlistEnabledInternally' in venue) {
      if (!data.config) data.config = {};
      data.config.defaultGuestlistEnabledInternally = venue.defaultGuestlistEnabledInternally;
    }

    if ('defaultGuestlistEnabledExternally' in venue) {
      if (!data.config) data.config = {};
      data.config.defaultGuestlistEnabledExternally = venue.defaultGuestlistEnabledExternally;
    }

    if ('guestlistMax' in venue) {
      if (!data.config) data.config = {};
      data.config.guestlistMax = venue.guestlistMax;
    }

    if (venue.timezone || venue.postalCode || venue.country || venue.address || venue.city || venue.state) {
      if (venue.timezone || venue.country) {
        data.location = {};
        if (venue.timezone) data.location.timezone = venue.timezone;
        if (venue.country) {
          data.location.country = venue.country.key;
          data.location.countryCode = venue.country.value;
        }
      }

      if (venue.address || venue.city || venue.state || venue.postalCode) {
        data.address = {};
        if (venue.address) data.address.address = venue.address;
        if (venue.city) data.address.city = venue.city;
        if (venue.state) data.address.state = venue.state;
        if (venue.postalCode) data.address.zip = venue.postalCode;
      }
    }

    data.tags = venue ? venue.tags : {};

    if (venue.venueTypeTag) {
      data.tags = _.reject(data.tags, function(tag) {
        return tag.key === 'VENUE_TYPE';
      });
      const venueTypeTag = _.findWhere(tags, {
        key: 'VENUE_TYPE',
        value: venue.venueTypeTag
      });
      if (venueTypeTag) data.tags.push(venueTypeTag);
    }

    if (venue.venueDressCodeOkTags) {
      data.tags = _.reject(data.tags, function(tag) {
        return tag.key === 'DRESS_CODE_OK';
      });
      data.tags = data.tags.concat(venue.venueDressCodeOkTags);
    }

    if (venue.venueDressCodeNotOkTags) {
      data.tags = _.reject(data.tags, function(tag) {
        return tag.key === 'DRESS_CODE_NOT_OK';
      });
      data.tags = data.tags.concat(venue.venueDressCodeNotOkTags);
    }

    if ('isMapWidgetEnabled' in venue) {
      data.eventDefault = venue.eventDefault || {};
      data.eventDefault.isMapWidgetEnabled = venue.isMapWidgetEnabled;
    }

    return data;
  };

  this.mapApiToVenue = function(data, tags, countries) {
    data.location = data.location || {};
    data.location.address = data.location.address || {};
    data.location.country = _.findWhere(countries, {
      value: data.location.countryCode
    });

    data.notificationEmails = (data.notificationEmails || []).join(',');

    data.defaultPoster = data.config && data.config.defaultPoster ? data.config.defaultPoster : null;

    data.trackingFacebookPixelId = data.trackingFacebookPixelId;
    data.inventoryTimeframe = data.inventoryTimeframe;
    data.inventorySortOrder = data.inventorySortOrder;
    data.details = data.details || {};
    data.details.social = data.details.social || {};
    data.venueTypeTag = _.findWhere(data.tags, {
      key: 'VENUE_TYPE'
    });

    if (data.venueTypeTag) data.venueTypeTag = data.venueTypeTag.value;

    data.venueDressCodeOkTags = _.filter(data.tags, {
      key: 'DRESS_CODE_OK'
    });

    data.venueDressCodeNotOkTags = _.filter(data.tags, {
      key: 'DRESS_CODE_NOT_OK'
    });

    if (data.tax) data.tax = Number((data.tax * 100).toFixed(4));
    if (data.ticketTax) data.ticketTax = Number((data.ticketTax * 100).toFixed(4));
    if (data.tip) data.tip = Number((data.tip * 100).toFixed(2));

    if (data.schedule) {
      _formatVenueSchedule(data.schedule);
    }

    if (data.branding) {
      data.primaryColor = _convertApiObjToRgb(data.branding.primaryColor);
      data.secondaryColor = _convertApiObjToRgb(data.branding.secondaryColor);
    }

    if (data.fees) {
      data.fees = _.map(data.fees, fee => {
        if (fee.type === 'PERCENT') {
          fee.amount = Number((fee.amount * 100).toFixed(2));
        }
        return fee;
      });
    }
    if (data.ticketFees) {
      data.ticketFees = _.map(data.ticketFees, fee => {
        if (fee.type === 'PERCENT') {
          fee.amount = Number((fee.amount * 100).toFixed(2));
        }
        return fee;
      });
    }

    if (data.config && data.config.reservationNote) {
      data.reservationNoteEnabled = true;
      data.reservationNote = data.config.reservationNote;
    } else {
      data.reservationNoteEnabled = false;
    }

    if (data.config && data.config.guestlistNote) {
      data.guestlistNoteEnabled = true;
      data.guestlistNote = data.config.guestlistNote;
    } else {
      data.guestlistNoteEnabled = false;
    }

    if (data.config && data.config.guestlistMax) {
      data.guestlistMax = data.config.guestlistMax;
    }

    if (data.eventDefault) {
      if (data.eventDefault.isMapWidgetEnabled) data.isMapWidgetEnabled = true;
    }

    return data;
  };

  this.mapAdmissionGroupToUI = function(admissionGroup) {
    if (!admissionGroup) throw new Error('mapperService - mapAdmissionGroupToUI() - admissionGroup is required');

    const data = {};

    if (admissionGroup.date) {
      data.date = _createUTCMomentFromIsoOrUnix(admissionGroup.date);
      if (!data.date.isValid()) throw new Error(this.ERRORS.DATE_INVALID_DATE);
    }

    data.event = admissionGroup.event;
    data.user = admissionGroup.user;
    data.venue = admissionGroup.venue;
    data.customer = admissionGroup.customer;
    data.createdById = admissionGroup.createdById;
    data.createdByName = admissionGroup.createdByName;
    data.createdByType = admissionGroup.createdByType;
    data.name = admissionGroup.name;
    data.email = admissionGroup.email;
    data.phone = admissionGroup.phone;
    data.minimumSpend = admissionGroup.minimumSpend;
    data.minimumBottles = admissionGroup.minimumBottles;
    data.customer = admissionGroup.customer;
    data.server = admissionGroup.server;
    data.serverName = admissionGroup.serverName;
    data.orderSummary = admissionGroup.orderSummary;
    data.guestsExpected = admissionGroup.guestsExpected;
    data.guestsExpectedMale = admissionGroup.guestsExpectedMale;
    data.guestsExpectedFemale = admissionGroup.guestsExpectedFemale;
    data.guestsExpectedUnisex = admissionGroup.guestsExpectedUnisex;
    data.tables = admissionGroup.tables;
    data.notes = admissionGroup.notes;
    data.status = admissionGroup.status;
    data.closed = admissionGroup.closed;
    data.arrived = admissionGroup.arrived;
    data.canceled = admissionGroup.canceled;
    data.comp = admissionGroup.comp;
    data.compType = admissionGroup.compType;
    data.vip = admissionGroup.vip;
    data.partial = admissionGroup.partial;
    data.prepaid = admissionGroup.prepaid;
    data.gender = admissionGroup.gender;
    data.inventoryPrice = admissionGroup.inventoryPrice;
    data.inventoryDescription = admissionGroup.inventoryDescription;
    data.inventory = admissionGroup.inventory;
    data.organization = admissionGroup.organization;
    data.spent = admissionGroup.spent;
    data.type = admissionGroup.type;
    data.compedBottles = admissionGroup.compedBottles;
    data.compedTickets = admissionGroup.compedTickets;
    data.birthdayYear = admissionGroup.birthdayYear;
    data.birthdayMonth = admissionGroup.birthdayMonth;
    data.birthdayDay = admissionGroup.birthdayDay;
    data.paymentDetail = admissionGroup.paymentDetail;
    data.paymentBreakdown = admissionGroup.paymentBreakdown;
    data.outgoingPayment = admissionGroup.outgoingPayment;
    data.receipts = admissionGroup.receipts;
    data.attributionName = admissionGroup.attributionName;
    data.attributionType = admissionGroup.attributionType;
    data.attributionId = admissionGroup.attributionId;
    data.posSyncEnabled = admissionGroup.posSyncEnabled;

    if (admissionGroup.arrivalTime) {
      data.arrivalTime = _createUTCMomentFromIsoOrUnix(admissionGroup.arrivalTime).format('h:mm A');
    }

    if (admissionGroup.attributionId && admissionGroup.attributionType) {
      data.attribution = {
        id: admissionGroup.attributionId,
        type: admissionGroup.attributionType,
        name: admissionGroup.attributionName
      };
    }

    return data;
  };

  /**
   * @param {Object} admissionGroup - admissionGroup data to map to API representation
   * @param {Object} [options]
   * @param {Boolean} [options.create] - map for admissionGroup create
   */
  this.mapAdmissionGroupToAPI = function(admissionGroup) {
    if (!admissionGroup) throw new Error('mapperService - mapAdmissionGroupToAPI() - admissionGroup is required');

    const data = {};

    if ('venue' in admissionGroup) data.venue = admissionGroup.venue;
    if ('event' in admissionGroup) data.event = admissionGroup.event;
    if ('type' in admissionGroup) data.type = admissionGroup.type;
    if ('guestsExpectedUnisex' in admissionGroup) data.guestsExpectedUnisex = admissionGroup.guestsExpectedUnisex;
    if ('guestsExpectedMale' in admissionGroup) data.guestsExpectedMale = admissionGroup.guestsExpectedMale;
    if ('guestsExpectedFemale' in admissionGroup) data.guestsExpectedFemale = admissionGroup.guestsExpectedFemale;
    if ('compedTickets' in admissionGroup) data.compedTickets = admissionGroup.compedTickets;
    if ('compedBottles' in admissionGroup) data.compedBottles = admissionGroup.compedBottles;
    if ('email' in admissionGroup) data.email = admissionGroup.email;
    if ('phone' in admissionGroup) data.phone = admissionGroup.phone;
    if ('firstName' in admissionGroup) data.firstName = admissionGroup.firstName;
    if ('lastName' in admissionGroup) data.lastName = admissionGroup.lastName;
    if ('name' in admissionGroup) data.name = admissionGroup.name;
    if ('inventory' in admissionGroup) data.inventory = admissionGroup.inventory;
    if ('birthdayDay' in admissionGroup) data.birthdayDay = admissionGroup.birthdayDay;
    if ('birthdayMonth' in admissionGroup) data.birthdayMonth = admissionGroup.birthdayMonth;
    if ('birthdayYear' in admissionGroup) data.birthdayYear = admissionGroup.birthdayYear;
    if ('notes' in admissionGroup) data.notes = admissionGroup.notes;
    if ('vip' in admissionGroup) data.vip = admissionGroup.vip;
    if ('comp' in admissionGroup) data.comp = admissionGroup.comp;
    if ('compType' in admissionGroup) data.compType = admissionGroup.compType;
    if ('partial' in admissionGroup) data.partial = admissionGroup.partial;
    if ('canceled' in admissionGroup) data.canceled = admissionGroup.canceled;
    if ('gender' in admissionGroup) data.gender = admissionGroup.gender;
    if ('customer' in admissionGroup) data.customer = admissionGroup.customer;
    if ('minimumSpend' in admissionGroup) data.minimumSpend = admissionGroup.minimumSpend;
    if ('minimumBottles' in admissionGroup) data.minimumBottles = admissionGroup.minimumBottles;
    if ('tables' in admissionGroup) data.tables = admissionGroup.tables;
    if ('attributionId' in admissionGroup) data.attributionId = admissionGroup.attributionId;
    if ('attributionType' in admissionGroup) data.attributionType = admissionGroup.attributionType;
    if ('serverId' in admissionGroup) data.serverId = admissionGroup.serverId;
    if ('receipts' in admissionGroup) data.receipts = admissionGroup.receipts;
    if (admissionGroup.enableConfirm) {
      data.status = 'CONFIRMED';
    }
    if ('date' in admissionGroup) {
      if (!_.isDate(admissionGroup.date) && !moment.isMoment(admissionGroup.date)) throw new Error(this.ERRORS.DATE_DATE_OR_MOMENT);
      admissionGroup.date = _.isDate(admissionGroup.date) ? moment.utc(admissionGroup.date) : admissionGroup.date;
      data.date = dateUtilsService.toUTCTimestampWithoutTime(admissionGroup.date);

      if ('arrivalTime' in admissionGroup) {
        data.arrivalTime = dateUtilsService.toUTCTimestampWithTime(new Date(admissionGroup.date.clone().format('MM/DD/YYYY') + ' ' + admissionGroup.arrivalTime)); //eslint-disable-line
      }
    }

    return data;
  };
}

function _getNotificationEmails(notificationEmails) {
  let notificationEmailArray = [];

  if (notificationEmails) {
    notificationEmailArray = notificationEmails.split(',');

    for (let i = 0; i < notificationEmailArray.length; i++) { //eslint-disable-line
      notificationEmailArray[i] = notificationEmailArray[i].toLowerCase().trim();
    }
  }

  return notificationEmailArray;
}

function _convertRgbToApiObj(rgb) {
  if (!rgb) return null;

  const numberPattern = /\d+/g;

  const parts = rgb.split(',');

  return parts && parts.length === 3 ? {
    red: parts[0].match(numberPattern)[0],
    green: parts[1].match(numberPattern)[0],
    blue: parts[2].match(numberPattern)[0]
  } : null;
}

function _convertApiObjToRgb(apiObj) {
  if (!apiObj) return null;

  return `rgb(${apiObj.red},${apiObj.green},${apiObj.blue})`;
}

function _formatVenueSchedule(schedule) {
  _.each(schedule, daySchedule => {
    if (!daySchedule.closed && (daySchedule.hours && daySchedule.hours.length)) {
      _.each(daySchedule.hours, hours => {
        const start = moment.utc(new Date(hours.start)).format('hh:mm A');
        const end = moment.utc(new Date(hours.end)).format('hh:mm A');

        hours.start = start;
        hours.end = end;
      });
    }
  });
}

/**
 * @param {String} dateStart - either an ISO date string, or a unix ms timestamp
 */
function _createUTCMomentFromIsoOrUnix(dateString) {
  let momentObj = moment.utc(dateString, 'x', true);

  if (!momentObj.isValid()) momentObj = moment.utc(dateString);

  return momentObj;
}

function _convertToUrl(path) {
  if (!path) throw new Error('mapperService - _convertToUrl() - path is required');

  if ((path.indexOf('http://') >= 0) || (path.indexOf('https://') >= 0)) {
    return path;
  }

  return `http://${path}`;
}

MapperService.$inject = [
  'dateUtilsService',
  'visibilityHelpersService'
];

export default MapperService;



// WEBPACK FOOTER //
// ./src/services/mapperService.js